import { useState } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, deletePackage, setTenderDue, packageTenderSync, getLatestVoteRound, voteBlocksInvite, staffCompliancePackage, type StaffComplianceView, type ApiError, type VoteRoundView, PackageDetail as Pkg, STAGE_LABEL, rmb } from '../lib/api';
import { ComparisonTable } from '../components/ComparisonTable';
import { WorkItemsPanel } from '../components/WorkItemsPanel';
import { PackageFilesPanel } from '../components/PackageFilesPanel';
import { DiscussionsThread } from '../components/DiscussionsThread';
import { XlsImportModal } from '../components/XlsImportModal';
import { BrainPanel } from '../components/BrainPanel';
import { InternalBidEntry } from '../components/InternalBidEntry';
import { InviteContractors } from '../components/InviteContractors';
import { ExecutionPanel } from '../components/ExecutionPanel';
import StandardsWorkflowCard from '../components/StandardsWorkflowCard';
import CompliancePanel from '../components/CompliancePanel';
import InstructionsPanel from '../components/InstructionsPanel';
import VotePanel from '../components/VotePanel';
import CreateTaskHere from '../components/CreateTaskHere';

type Tab = 'compare' | 'work_items' | 'files' | 'discussion' | 'brain' | 'standards' | 'compliance' | 'instructions' | 'execution';
// spec §4: 招标文件 (files) precedes 工程量清单 (work_items) — the pulled zip lands on 招标文件 first,
// then its 工程清单 is filled into work_items. ?tab= keys are unchanged.
// Eric 9/18 (compliance file): 履约档案 sits AFTER 验收标准与合同 and before 执行与结算 — the paper the
// contract requires comes between the signature and the money.
// Eric 9/18 (工程指令): 工程指令 sits AFTER 履约档案 and before 执行与结算 — the orders given mid-job
// are what changes the number the execution tab then pays against.
const TABS: Tab[] = ['compare', 'files', 'work_items', 'discussion', 'brain', 'standards', 'compliance', 'instructions', 'execution'];

/** ISO (or now+24h when unset) → the local-timezone string a datetime-local input expects. */
function toLocalInput(iso: string | null | undefined): string {
  const d = iso ? new Date(iso) : new Date(Date.now() + 24 * 3600 * 1000);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

export default function PackageDetail() {
  const { pid } = useParams<{ pid: string }>();
  const qc = useQueryClient();
  const nav = useNavigate();
  // Tab lives in the URL (?tab=) so a task's "Open source" deep-link reopens the exact view. replace:true
  // keeps tab-flipping out of the browser history.
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab') as Tab | null;
  const tab: Tab = rawTab && TABS.includes(rawTab) ? rawTab : 'compare';
  const setTab = (t: Tab) => setSearchParams((prev) => { prev.set('tab', t); return prev; }, { replace: true });
  const [showImport, setShowImport] = useState(false);
  const [fillStaging, setFillStaging] = useState<string | null>(null);   // 采购大脑「一键填充」 staging id (null = closed)
  const [showEntry, setShowEntry] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [dueEdit, setDueEdit] = useState<string | null>(null);   // fb_07c3fe4a · 截标时间 editor (null = closed)
  const { data: pkg, isLoading, isError, error } = useQuery({
    queryKey: ['package', pid],
    queryFn: () => api<Pkg>(`/packages/${pid}`),
    enabled: !!pid,
  });
  // Labor-tender ingest ledger for THIS package (spec §7): drives 「一键填充」 + the 招标文件-tab sync line.
  const { data: tenderSync } = useQuery({
    queryKey: ['package-tender-sync', pid],
    queryFn: () => packageTenderSync(pid!),
    enabled: !!pid,
  });
  // 定标投票 (§6): the same query VotePanel reads — an OPEN round hard-blocks 邀请劳务 (server 409s too).
  const { data: voteView } = useQuery<VoteRoundView>({
    queryKey: ['vote-round', pid],
    queryFn: () => getLatestVoteRound(pid!),
    enabled: !!pid,
  });
  // Eric 9/18 (compliance file): the 待审 pill on the 履约档案 tab. Gated on awarded_engagement_id so a
  // package still in tender does NOT pay for an extra request — the 履约档案 cannot exist before award,
  // and the read is shared (same queryKey) with CompliancePanel once the tab is opened.
  const { data: compliancePill } = useQuery<StaffComplianceView>({
    queryKey: ['compliance', 'package', pid],
    queryFn: () => staffCompliancePackage(pid!),
    enabled: !!pid && !!pkg?.awarded_engagement_id,
  });

  if (isLoading) return <div className="empty">加载中… / Loading…</div>;
  if (isError) return <div className="empty" style={{ color: 'var(--bad)' }}>{String((error as any)?.message)}</div>;
  if (!pkg) return <div className="empty">未找到 / Not found</div>;

  // 改改 6/29: delete a package (e.g. an accidental duplicate). Server protects one with bids unless forced.
  async function handleDelete() {
    if (!pid || !pkg) return;
    if (!confirm(`删除采购包「${pkg.name}」？\n将一并删除清单、报价、对比等全部数据，不可恢复。\n/ Delete this package and ALL its data (BOQ, bids, comparison)? Cannot be undone.`)) return;
    try {
      await deletePackage(pid);
      await qc.invalidateQueries({ queryKey: ['packages'] });
      nav('/');
    } catch (e) {
      const b = (e as ApiError)?.body;
      if (b?.error === 'has_bids') {
        if (!confirm(`本包已有 ${b.bids} 条报价，删除将一并清除且不可恢复。确定删除？/ This package has ${b.bids} bids — delete them too?`)) return;
        try { await deletePackage(pid, true); await qc.invalidateQueries({ queryKey: ['packages'] }); nav('/'); }
        catch (e2) { alert(String((e2 as Error)?.message ?? e2)); }
      } else { alert(String((e as Error)?.message ?? e)); }
    }
  }

  const awardedEng = pkg.engagements.find((e) => e.award_status === 'awarded');
  const awardedBidTotal = awardedEng
    ? (awardedEng.bids.reduce((b, x) => (x.round_number > (b?.round_number ?? -1) ? x : b), undefined as typeof awardedEng.bids[number] | undefined)?.total_rmb ?? null)
    : null;
  const withdrawn = pkg.engagements.filter((e) => e.withdrawn);
  // 采购大脑 pull ready to review: a staged ledger row that already carries its staging id (set by Stage 3).
  const stagedPull = tenderSync?.rows.find((r) => r.status === 'staged' && r.staging_id);
  const voteBlocked = voteBlocksInvite(voteView);   // §6 · 票没投齐 → 邀请劳务 stays shut

  return (
    <div>
      <div className="small"><Link to="/">← 全部采购包 / All packages</Link></div>
      <h2 style={{ margin: '8px 0 2px' }}>{pkg.name}</h2>
      <div className="row-wrap" style={{ marginBottom: 6 }}>
        <span className="muted">{pkg.project_name}</span>
        <span className="pill pill-muted">{pkg.procurement_mode}</span>
        {pkg.awarded_engagement_id
          ? <span className="pill pill-good">已定标 {awardedEng?.labor_co_name ? `· ${awardedEng.labor_co_name}` : ''}</span>
          : <span className="pill pill-stage">{STAGE_LABEL[pkg.current_stage] ?? `stage ${pkg.current_stage}`}</span>}
        {pkg.tender_due_at && (pkg.sealed
          ? <span className="pill pill-muted" title={`截标 / due ${new Date(pkg.tender_due_at).toLocaleString('zh-CN')}`}>🔒 未开标 / Sealed</span>
          : <span className="pill pill-good">🔓 已开标 / Opened</span>)}
        {/* fb_07c3fe4a (改改): the deadline was create-only — set/change/clear it right here. Past the
            deadline, labor can no longer submit online bids OR upload 报价文件 (server-enforced). */}
        {dueEdit != null ? (
          <span className="row" style={{ gap: 4, alignItems: 'center' }}>
            <input type="datetime-local" value={dueEdit} onChange={(e) => setDueEdit(e.target.value)} />
            <button className="small btn-primary" disabled={!dueEdit} onClick={async () => {
              try { await setTenderDue(pid!, new Date(dueEdit).toISOString()); await qc.invalidateQueries({ queryKey: ['package', pid] }); setDueEdit(null); }
              catch (e) { alert(String((e as ApiError)?.body?.error ?? (e as Error)?.message ?? e)); }
            }}>✓ 保存</button>
            {pkg.tender_due_at && <button className="small" title="清除截标时间（重新开放报价与上传）" onClick={async () => {
              try { await setTenderDue(pid!, null); await qc.invalidateQueries({ queryKey: ['package', pid] }); setDueEdit(null); }
              catch (e) { alert(String((e as ApiError)?.body?.error ?? (e as Error)?.message ?? e)); }
            }}>清除</button>}
            <button className="small" onClick={() => setDueEdit(null)}>×</button>
          </span>
        ) : (
          <button className="small" title="投标截止后，劳务将无法在线报价或上传报价文件 / after the deadline labor can no longer bid or upload" onClick={() => setDueEdit(toLocalInput(pkg.tender_due_at))}>
            ⏰ {pkg.tender_due_at ? `截标 ${new Date(pkg.tender_due_at).toLocaleString('zh-CN', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })} ✎` : '设截标时间 / Set deadline'}
          </button>
        )}
        <span className="muted small">测算价 / envelope {rmb(pkg.budget_envelope_rmb)}</span>
      </div>
      {pkg.budget_envelope_notes_md && <div className="small muted" style={{ marginBottom: 10 }}>{pkg.budget_envelope_notes_md}</div>}
      {withdrawn.length > 0 && <div className="small muted" style={{ marginBottom: 10 }}>退出 / withdrawn: {withdrawn.map((e) => e.labor_co_name).join(', ')}</div>}

      <div className="row" style={{ gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
        <button className={`tab${tab === 'compare' ? ' active' : ''}`} onClick={() => setTab('compare')}>📊 对比与定标 / Compare & Award</button>
        <button className={`tab${tab === 'files' ? ' active' : ''}`} onClick={() => setTab('files')}>📎 招标文件 / Files{stagedPull ? ' ·📥' : ''}</button>
        <button className={`tab${tab === 'work_items' ? ' active' : ''}`} onClick={() => setTab('work_items')}>📋 工程清单 / Work Items ({pkg.work_items.length})</button>
        <button className={`tab${tab === 'discussion' ? ' active' : ''}`} onClick={() => setTab('discussion')}>💬 招标群 / Discussion ({pkg.discussions.length})</button>
        <button className={`tab${tab === 'brain' ? ' active' : ''}`} onClick={() => setTab('brain')}>🧠 采购大脑 / Brain{pkg.wtl_project_uuid ? ' ·🔗' : ''}</button>
        <button className={`tab${tab === 'standards' ? ' active' : ''}`} onClick={() => setTab('standards')}>📐 验收标准与合同 / Standards</button>
        <button className={`tab${tab === 'compliance' ? ' active' : ''}`} onClick={() => setTab('compliance')}>
          📁 履约档案 / Compliance file
          {/* the pill is the only ambient signal that a 资质 is sitting unreviewed — it is why the read
              runs at all before you open the tab. Awarded packages only (see the query below). */}
          {(compliancePill?.counts.awaiting_review ?? 0) > 0 && (
            <span className="pill pill-bad small" style={{ marginLeft: 6 }}>{compliancePill!.counts.awaiting_review}</span>
          )}
        </button>
        <button className={`tab${tab === 'instructions' ? ' active' : ''}`} onClick={() => setTab('instructions')}>📝 工程指令 / Instructions</button>
        <button className={`tab${tab === 'execution' ? ' active' : ''}`} onClick={() => setTab('execution')}>💰 执行与结算 / Execution</button>
        <span className="spacer" />
        <button
          onClick={() => setShowInvite(true)}
          disabled={voteBlocked}
          title={voteBlocked
            ? (voteView!.round!.phase === 'nominating'
              ? `定标投票还在推荐阶段（已推荐 ${voteView!.voters.filter((v) => v.nominated_at).length}/${voteView!.voters.length}），名单汇总并投票完成后才能邀请劳务 / the panel is still nominating — inviting unlocks after the vote`
              : `定标投票未完成（已投 ${voteView!.voters.filter((v) => v.voted_at).length}/${voteView!.voters.length}），投票齐了才能邀请劳务 / voting incomplete — all votes must be in before inviting`)
            : '从供应商库邀请劳务方 / invite suppliers from the registry'}
        >🤝 邀请劳务 / Invite{voteBlocked ? (voteView!.round!.phase === 'nominating' ? ' · 推荐中' : ' · 投票未完成') : ''}</button>
        <button onClick={() => setShowEntry(true)}>✍️ 录入报价 / Enter bid</button>
        {stagedPull && (
          <button className="btn-primary" title="采购大脑已拉取该项目的工程量清单，点此核对并一键填充 / a BOQ was pulled from 采购大脑 — review and fill it in"
            onClick={() => setFillStaging(stagedPull.staging_id!)}>📥 一键填充（采购大脑拉取）/ Fill from pulled BOQ</button>
        )}
        <button onClick={() => setShowImport(true)}>📥 从 xls 导入 / Import xls</button>
        <CreateTaskHere
          context={{ kind: 'package', label: pkg.name, package_id: pkg.id, view_tab: tab }}
          packageId={pkg.id}
          pageUrl={`/packages/${pkg.id}?tab=${tab}`}
          prefillTitle={`关于「${pkg.name}」：`}
        />
        <button onClick={handleDelete} title="删除整个采购包（含清单/报价）/ delete this package" style={{ color: 'var(--bad)' }}>🗑 删除包 / Delete</button>
      </div>

      {tab === 'compare' && <><VotePanel pid={pkg.id} /><ComparisonTable pkg={pkg} voteBlocked={voteBlocked} /></>}
      {tab === 'work_items' && <WorkItemsPanel items={pkg.work_items} pid={pkg.id} boqRevision={pkg.boq_revision ?? 0} onChange={() => qc.invalidateQueries({ queryKey: ['package', pid] })} />}
      {tab === 'files' && <PackageFilesPanel pid={pkg.id} syncRows={tenderSync?.rows} />}
      {tab === 'discussion' && <DiscussionsThread pid={pkg.id} discussions={pkg.discussions} />}
      {tab === 'brain' && <BrainPanel pkg={pkg} />}
      {tab === 'standards' && <StandardsWorkflowCard pid={pkg.id} isLocked={pkg.engagements.some((e) => !!e.final_confirmed_at)} />}
      {tab === 'compliance' && <CompliancePanel pid={pkg.id} />}
      {tab === 'instructions' && <InstructionsPanel pid={pkg.id} workItems={pkg.work_items} />}
      {tab === 'execution' && <ExecutionPanel pid={pkg.id} isAwarded={!!pkg.awarded_engagement_id} awardedBidTotal={awardedBidTotal} awardedTaxPct={awardedEng?.final_tax_pct ?? null} />}

      {showInvite && <InviteContractors pkg={pkg} onClose={() => setShowInvite(false)} vote={voteView} />}
      {showEntry && <InternalBidEntry pkg={pkg} onClose={() => setShowEntry(false)} />}
      {showImport && <XlsImportModal pid={pkg.id} onClose={() => setShowImport(false)} />}
      {fillStaging && <XlsImportModal pid={pkg.id} stagingId={fillStaging} onClose={() => setFillStaging(null)} />}
    </div>
  );
}
